import React, { useContext } from 'react';
import { useDeletePosteMutation } from '../../../services/SkillsManagement';
import { useDispatch } from 'react-redux';
import { DELETE_POSTE } from '../../../Reducers/Actions';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import { MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { SnackBarContext } from '../../../Context/SnackBarContextProvider';
import MuiDialog from '../../../SystemDesign/MuiDialog';

/**
 * DeletePoste Component - Allows deleting a poste
 * @param {Object} props
 * @param {Object} props.row - The row object containing the poste data
 */
const DeletePoste = ({ row }) => {
    // Use RTK Query mutation hook directly - will be connected to Redux
    const [deletePoste, { isLoading }] = useDeletePosteMutation();
    const dispatch = useDispatch();
    
    // Access the SnackBar context
    const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);

    // Safely access row data
    const poste = row?.original || {};
    
    if (!poste || !poste.posteId) return null;
    
    const handleDelete = async () => {
        try {
            try {
                // Call the API to delete the poste
                const response = await deletePoste({ posteId: poste.posteId }).unwrap();
                
                // Update Redux store IMMEDIATELY after successful API call
                dispatch({ type: DELETE_POSTE, payload: { posteId: poste.posteId } });
                
                // Extract the message string properly from the response
                let successMessage;
                if (typeof response === 'string') {
                    successMessage = response;
                } else if (response && typeof response === 'object' && response.message) {
                    successMessage = response.message;
                } else {
                    successMessage = `Poste "${poste.name || 'Unknown'}" supprimé avec succès`;
                }
                
                // Show success message
                setInfoAlert({
                    severity: 'success',
                    message: successMessage
                });
                setOpenSnackBar(true);
                
                return true; // Indicate successful deletion
            } catch (parseError) {
                // If we got a parsing error but the status is 200, the deletion was successful
                // but the API returned a plain text response instead of JSON
                if (parseError?.originalStatus === 200) {
                    // The deletion was successful despite the parsing error
                    // Update Redux store
                    dispatch({ type: DELETE_POSTE, payload: { posteId: poste.posteId } });
                    
                    // Show success message using the text response if available
                    const successMessage = parseError.data || `Poste "${poste.name || 'Unknown'}" supprimé avec succès`;
                    
                    setInfoAlert({
                        severity: 'success',
                        message: successMessage
                    });
                    setOpenSnackBar(true);
                    
                    return true; // Indicate successful deletion
                }
                throw parseError; // Re-throw if it's not a 200 status
            }
        } catch (error) {
            console.error('Error deleting poste:', error);
            // Extract error message properly
            let errorMessage = 'Une erreur est survenue lors de la suppression';
            
            if (error?.data?.message) {
                errorMessage = typeof error.data.message === 'string' ? error.data.message : JSON.stringify(error.data.message);
            } else if (error?.message) {
                errorMessage = error.message;
            }
            
            // Show error message
            setInfoAlert({
                severity: 'error',
                message: errorMessage
            });
            setOpenSnackBar(true);
            
            return false; // Indicate failed deletion
        }
    };

    const triggerButton = 
        <MenuItem>
            <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
        </MenuItem>

    const dialogContent = (
        <div>
            <p>Are you sure you want to delete {poste.name} ?</p>
            <p>This action is irreversible</p>
        </div>
    );

    return (
        <MuiDialog
            icon={<DeleteOutlineIcon />}
            triggerButton={triggerButton}
            title="Supprimer"
            subtitle={`Vous êtes sur le point de supprimer ${poste.name}`}
            content={dialogContent}
            maxWidth="xs"
            existAction={true}
            actionTitle="Supprimer"
            actionFunction={handleDelete}
            isLoading={isLoading}
        />
    );
};

// Add static properties for menu integration
DeletePoste.menuIcon = <DeleteOutlineIcon fontSize="small" />;
DeletePoste.menuLabel = "Delete";

export default DeletePoste;